// SPDX-License-Identifier: AGPL-3.0-or-later

import * as MessageCommands from '@app/features/messaging/commands/MessageCommands';
import {SafeMarkdown} from '@app/features/messaging/components/markdown';
import type {Message as MessageModel} from '@app/features/messaging/models/MessagingMessage';
import Vault, {getVaultSessionKey} from '@app/features/vault/state/Vault';
import {
	cacheVaultPlaintext,
	decryptVaultText,
	getVaultEnvelopeExpiry,
	isVaultEnvelope,
} from '@app/features/vault/VaultCrypto';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useRef, useState} from 'react';

interface VaultMessageContentProps {
	channelId: string;
	message: MessageModel;
	'data-flx'?: string;
}

export const VaultMessageContent = observer(({channelId, message, 'data-flx': dataFlx}: VaultMessageContentProps) => {
	const {i18n} = useLingui();
	const [decrypted, setDecrypted] = useState<string | null>(null);
	const [expired, setExpired] = useState(false);
	const removedRef = useRef(false);
	const sessionVersion = Vault.sessionVersion;
	const unlocked = Vault.isChannelUnlocked(channelId);
	const key = getVaultSessionKey();
	const content = message.content;
	const expiredLabel = i18n._(
		msg({
			message: '⌛ Message expired',
			comment: 'Shown for a vault message whose temporary lifetime has passed.',
		}),
	);

	const isExpired = useCallback((): boolean => {
		const expiry = getVaultEnvelopeExpiry(content);
		return expiry !== null && Date.now() > expiry;
	}, [content]);

	useEffect(() => {
		if (!isVaultEnvelope(content)) {
			setDecrypted(null);
			setExpired(false);
			return;
		}
		if (isExpired()) {
			setExpired(true);
			setDecrypted(null);
			if (message.isCurrentUserAuthor() && !removedRef.current) {
				removedRef.current = true;
				void MessageCommands.remove(channelId, message.id).catch(() => {});
			}
			return;
		}
		setExpired(false);
		if (key === null || !unlocked) {
			setDecrypted(null);
			return;
		}
		let cancelled = false;
		decryptVaultText(key, content)
			.then((text) => {
				if (!cancelled) {
					cacheVaultPlaintext(content, text);
					setDecrypted(text);
				}
			})
			.catch(() => {
				if (!cancelled) setDecrypted(null);
			});
		return () => {
			cancelled = true;
		};
	}, [channelId, content, isExpired, key, message, sessionVersion, unlocked]);

	if (!isVaultEnvelope(content)) {
		return <SafeMarkdown content={content} />;
	}
	if (expired) {
		return (
			<span className="vault-chip vault-chip-expired" data-flx={dataFlx ?? 'vault.message-content.expired'}>
				{expiredLabel}
			</span>
		);
	}
	if (key === null || !unlocked || decrypted === null) {
		return (
			<span className="vault-chip vault-chip-locked" data-flx={dataFlx ?? 'vault.message-content.locked'}>
				<Trans>🔒 Locked vault message</Trans>
			</span>
		);
	}
	return <SafeMarkdown content={decrypted} />;
});
